import React, { createContext, useContext, useLayoutEffect, useMemo, useState } from 'react';

import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeOptions, ThemeProvider as MUIThemeProvider } from '@mui/material/styles';
import { ThemeProvider } from 'styled-components';

import { getThemeObject, ThemeMode } from '../../theme';

const ThemeModeOverrideContext = createContext<(mode: ThemeMode | undefined) => void>(() => {});

export interface AppThemeProviderProps {
  /** The user's own mode. */
  mode: ThemeMode;
  /** Merged over the theme in either mode. */
  themeOptions?: ThemeOptions;
  children: React.ReactNode;
}

/**
 * The app's one theme, for styled-components and MUI alike, with the baseline
 * that paints the body from it. A screen drawn in a mode of its own switches
 * this theme rather than nesting another, so what renders outside the screen's
 * own box — a dialog, a menu, a tooltip, a snackbar — is drawn in its mode too.
 */
const AppThemeProvider = ({ mode, themeOptions, children }: AppThemeProviderProps) => {
  const [overrideMode, setOverrideMode] = useState<ThemeMode>();
  const activeMode = overrideMode ?? mode;
  // Built once per mode: createTheme is expensive, and a new theme identity
  // invalidates every styled component below both providers
  const theme = useMemo(
    () => createTheme(getThemeObject(activeMode), themeOptions ?? {}),
    [activeMode, themeOptions]
  );

  return (
    <ThemeModeOverrideContext.Provider value={setOverrideMode}>
      <ThemeProvider theme={theme}>
        <MUIThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </MUIThemeProvider>
      </ThemeProvider>
    </ThemeModeOverrideContext.Provider>
  );
};

/**
 * Draws the whole app in `mode` while the caller is mounted, whatever the
 * user's setting, for a screen whose mode is set by where it is shown rather
 * than by who is looking. Absent leaves the user's own. The user's setting is
 * never changed, so leaving the screen restores it.
 */
export const useThemeModeOverride = (mode?: ThemeMode) => {
  const setOverrideMode = useContext(ThemeModeOverrideContext);

  // Before paint, so the screen never flashes in the user's mode
  useLayoutEffect(() => {
    if (!mode) {
      return undefined;
    }
    setOverrideMode(mode);
    return () => setOverrideMode(undefined);
  }, [mode, setOverrideMode]);
};

export default AppThemeProvider;
